import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AutoDemo, { appear, typed, typingDone } from './AutoDemo';
import { AppFrame, AssistantPanel, EndCard, PlaceholderRows } from './demoKit';
import { marketDataDemoContent } from './marketDataDemoContent';

// Market data in 35 seconds: ask the AI search assistant about upcoming projects, see the projects and their
// signals, ask one follow-up and get the project's stages from the minutes. Replaces markkinadatavideo.mp4.

const ROW_GAP = 420;

const Backdrop = ({ c }) => <AppFrame active={c.pageTitle}><PlaceholderRows title={c.pageTitle} /></AppFrame>;
const Panel = ({ c, ...rest }) => <AssistantPanel title={c.assistant} tabs={c.tabs} placeholder={c.placeholder} {...rest} />;

export const ProjectsAnswer = ({ c, t, from = 0 }) => {
    const rowsAt = from + 1500;
    const signalsAt = rowsAt + c.projects.length * ROW_GAP + 400;
    return (
        <>
            <div className="dk-bubble" style={appear(t, from)}>{c.question}</div>
            <div className="dk-answer">
                <div className="dk-answer__title" style={appear(t, from + 300)}>{c.answerTitle}</div>
                <p className="dk-answer__lead" style={appear(t, from + 700)}>{c.answerLead}</p>
                <div className="dk-table">
                    <div className="dk-table__head" style={appear(t, from + 1200)}>{c.columns.map((col) => <span key={col}>{col}</span>)}</div>
                    {c.projects.map((row, i) => (
                        <div key={row[1]} className="dk-table__row" style={appear(t, rowsAt + i * ROW_GAP)}>
                            <span>{row[0]}</span><span>{row[1]}</span><span>{row[2]}</span>
                        </div>
                    ))}
                </div>
                <div className="dk-answer__subtitle" style={appear(t, signalsAt)}>{c.signalsTitle}</div>
                {c.signals.map(([place, text], i) => (
                    <div key={place} className="dk-signal" style={appear(t, signalsAt + 400 + i * 600)}>
                        <b>{place}</b> {text} <span className="dk-cite">{i + 1}</span>
                    </div>
                ))}
            </div>
        </>
    );
};

export const buildMarketDataScenes = (c) => {
    const askTyped = 900;
    const askSent = typingDone(c.question, askTyped) + 250;
    const signalsAt = 1500 + c.projects.length * ROW_GAP + 400;
    const followTyped = 400;
    const followSent = typingDone(c.followUp, followTyped) + 250;
    return [
        {
            id: 'ask', title: c.chapters[0], duration: askSent + 700,
            render: ({ t }) => (
                <>
                    <Backdrop c={c} />
                    <div className="dk-dim" style={{ opacity: Math.min(1, t / 300) }} />
                    <Panel c={c} style={appear(t, 150, 400)} input={typed(c.question, t, askTyped)} sending={t > askSent}>
                        <div className="dk-empty">{c.emptyHint}</div>
                    </Panel>
                </>
            ),
        },
        {
            id: 'projects', title: c.chapters[1], duration: signalsAt + 400 + c.signals.length * 600 + 3600,
            render: ({ t }) => (
                <>
                    <Backdrop c={c} />
                    <div className="dk-dim" />
                    {/* Scrolls like a chat once the signals arrive. */}
                    <Panel c={c} scroll={Math.max(0, Math.min(1, (t - signalsAt + 200) / 900))}>
                        <ProjectsAnswer c={c} t={t} />
                    </Panel>
                </>
            ),
        },
        {
            id: 'follow-up', title: c.chapters[2], duration: followSent + 700,
            render: ({ t }) => (
                <>
                    <Backdrop c={c} />
                    <div className="dk-dim" />
                    <Panel c={c} scroll={1} input={typed(c.followUp, t, followTyped)} sending={t > followSent}>
                        <ProjectsAnswer c={c} t={60000} />
                    </Panel>
                </>
            ),
        },
        {
            id: 'stages', title: c.chapters[3], duration: 900 + c.timeline.length * 900 + 3800,
            render: ({ t }) => (
                <>
                    <Backdrop c={c} />
                    <div className="dk-dim" />
                    <Panel c={c}>
                        <div className="dk-bubble" style={appear(t, 0)}>{c.followUp}</div>
                        <div className="dk-answer">
                            <div className="dk-answer__title" style={appear(t, 300)}>{c.timelineTitle}</div>
                            <ol className="dk-timeline">
                                {c.timeline.map(([date, text], i) => (
                                    <li key={date} style={appear(t, 900 + i * 900)}>
                                        <span className="dk-timeline__date">{date}</span>
                                        <span className="dk-timeline__text">{text} <span className="dk-cite">{c.source}</span></span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </Panel>
                </>
            ),
        },
        {
            id: 'end', title: c.chapters[4], duration: 5200,
            render: ({ t }) => <EndCard t={t} lines={c.endLines} />,
        },
    ];
};

const MarketDataDemo = ({ onExpand, autoPlay = true }) => {
    const { t, i18n } = useTranslation();
    const content = marketDataDemoContent(i18n.language);
    const scenes = useMemo(() => buildMarketDataScenes(content), [content]);
    return (
        <AutoDemo
            scenes={scenes}
            autoPlay={autoPlay}
            onExpand={onExpand}
            className="market-data-demo"
            label={t('pages.marketDataUpsell.videoCaption', 'Katso 30 sek: näin markkinadata toimii')}
            labels={{
                play: t('demo.play', 'Toista'),
                pause: t('demo.pause', 'Tauko'),
                expand: t('pages.home.expandVideo', 'Expand video'),
            }}
        />
    );
};

export default MarketDataDemo;
